import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { useForm } from 'react-hook-form'
import {
  Box, IconButton, Menu, MenuItem, Typography,
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { getLocations } from 'src/services/locations/locationsService'
import {
  getStockLocations,
  updateStockAddress,
  updateStockExpirationDate,
  updateStockQuantity,
} from 'src/services/stock/stockService'
import TableMui from 'src/components/TableMui'
import { selectAppIsLoading } from 'src/features/selector'
import { actions as appActions } from '../../features/state'
import { AdjustmentDialog } from './components/AdjustmentDialog'
import * as S from './Adjustments.styles'
import { LocationRecord, useLocationCascade } from './useLocationCascade'

dayjs.extend(utc)

interface StockLocationRecord {
  cod_produto: string
  data_validade: string
  descricao: string
  escaninho_nivel: string
  galpao: string
  id_estoque_lote: number
  'id_localização': number
  id_lote: number
  id_modelo: number
  posicao_pallete: string
  quantidade: number
  rua: string
}

interface StockLocationRow extends StockLocationRecord {
  endereco: string
  acao: JSX.Element
}

type AdjustmentType = 'address' | 'amount' | 'expirationDate' | null

interface FilterFormValues {
  barcodeUser: string
}

interface AddressFormValues {
  justification: string
}

interface AmountFormValues {
  amount: number
  justification: string
}

interface ExpirationDateFormValues {
  expirationDate: string
}

interface LastSearch {
  shed: string
  street: string
  shelf: string | null
  bin: string | null
  page: number
  perPage: number
  order?: string
  orderBy?: string | number
}

interface Pagination {
  currentPage: number
  perPage: number
  totalItems: number
  totalPages: number
}

const requiredMessage = 'Campo obrigatório'

const orderByMap: Record<string, string> = {
  id_lote: 'id_batch',
  cod_produto: 'product_code',
  descricao: 'product_description',
  quantidade: 'quantity',
  data_validade: 'expiration_date',
  endereco: 'address',
}

const dialogTitles: Record<Exclude<AdjustmentType, null>, string> = {
  address: 'Ajuste Endereço',
  amount: 'Ajuste Quantidade',
  expirationDate: 'Ajuste Data de Validade',
}

interface RowActionsMenuProps {
  onSelect: (type: AdjustmentType) => void
}

const RowActionsMenu = ({ onSelect }: RowActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleSelect = (type: AdjustmentType) => {
    setAnchorEl(null)
    onSelect(type)
  }

  return (
    <>
      <IconButton
        aria-label='Ações'
        size='small'
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleSelect('amount')}>Ajuste de Quantidade</MenuItem>
        <MenuItem onClick={() => handleSelect('address')}>Ajuste de Endereço</MenuItem>
        <MenuItem onClick={() => handleSelect('expirationDate')}>Ajuste Data de Validade</MenuItem>
      </Menu>
    </>
  )
}

const Adjustments = () => {
  const dispatch = useDispatch()
  const isLoading = useSelector(selectAppIsLoading)
  const { fc, validateFc } = useFulfillmentCenter()

  const [locations, setLocations] = useState<LocationRecord[]>([])
  const [stockLocations, setStockLocations] = useState<StockLocationRecord[]>([])
  const [barcodeUser, setBarcodeUser] = useState('')
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(null)
  const [adjustmentItem, setAdjustmentItem] = useState<StockLocationRecord | null>(null)
  const [adjustmentError, setAdjustmentError] = useState<string | undefined>()
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1, perPage: 15, totalItems: 0, totalPages: 0,
  })

  const lastSearch = useRef<LastSearch | null>(null)
  const preventDoubleFetch = useRef(false)
  const preventDoubleSubmit = useRef(false)
  const preventDoubleAdjustmentSubmit = useRef(false)

  const filterCascade = useLocationCascade(locations)
  const addressCascade = useLocationCascade(locations)

  const {
    register: registerFilter,
    handleSubmit: handleSubmitFilter,
    setError: setFilterError,
    formState: { errors: filterErrors },
  } = useForm<FilterFormValues>({ defaultValues: { barcodeUser: '' } })

  const {
    register: registerAddress,
    handleSubmit: handleSubmitAddress,
    reset: resetAddressForm,
    formState: { errors: addressErrors },
  } = useForm<AddressFormValues>({ defaultValues: { justification: '' } })

  const {
    register: registerAmount,
    handleSubmit: handleSubmitAmount,
    reset: resetAmountForm,
    formState: { errors: amountErrors },
  } = useForm<AmountFormValues>({ defaultValues: { amount: undefined, justification: '' } })

  const {
    register: registerExpirationDate,
    handleSubmit: handleSubmitExpirationDate,
    reset: resetExpirationDateForm,
    formState: { errors: expirationDateErrors },
  } = useForm<ExpirationDateFormValues>({ defaultValues: { expirationDate: '' } })

  useEffect(() => {
    validateFc()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateLocations = useCallback(async () => {
    const response = await getLocations(fc?.id)
    if (!response.isOk) {
      toast.error('Falha ao carregar localizações!')
      return
    }
    setLocations(response.data.localizacoes)
  }, [fc?.id])

  useEffect(() => {
    updateLocations()
  }, [updateLocations])

  const fetchStockLocations = async (
    badge: string,
    shed: string,
    street: string,
    shelf: string | null,
    bin: string | null,
    page: number,
    perPage: number,
    order?: string,
    orderBy?: string | number,
  ): Promise<void> => {
    if (preventDoubleFetch.current) return
    preventDoubleFetch.current = true

    try {
      dispatch(appActions.isLoading(true))
      const response = await getStockLocations(
        badge, fc?.id, shed, street, shelf, bin, page, perPage, order, orderBy,
      )
      if (!response.isOk) {
        if (response.data?.status_code === 401 && response.data?.error_code === 'UNAUTHORIZED') {
          setFilterError('barcodeUser', { message: 'Crachá inválido' })
          return
        }
        toast.error(response.data?.message || 'Falha ao carregar estoque!')
        return
      }
      setStockLocations(response.data.estoque)
      setPagination(response.data.pagination)
    } catch {
      toast.error('Falha ao carregar estoque!')
    } finally {
      dispatch(appActions.isLoading(false))
      preventDoubleFetch.current = false
    }
  }

  const refetchLastSearch = async (): Promise<void> => {
    if (!lastSearch.current) return
    await fetchStockLocations(
      barcodeUser,
      lastSearch.current.shed,
      lastSearch.current.street,
      lastSearch.current.shelf,
      lastSearch.current.bin,
      lastSearch.current.page,
      lastSearch.current.perPage,
      lastSearch.current.order,
      lastSearch.current.orderBy,
    )
  }

  const getPaginatedItems = (
    page: number,
    perPage: number,
    order?: string,
    orderBy?: string | number,
  ): void => {
    if (!lastSearch.current) return
    const mappedOrderBy = orderBy ? (orderByMap[orderBy] ?? orderBy) : orderBy
    lastSearch.current = {
      ...lastSearch.current, page, perPage, order, orderBy: mappedOrderBy,
    }
    fetchStockLocations(
      barcodeUser,
      lastSearch.current.shed,
      lastSearch.current.street,
      lastSearch.current.shelf,
      lastSearch.current.bin,
      page,
      perPage,
      order,
      mappedOrderBy,
    )
  }

  const onValidFilterSubmit = async ({ barcodeUser: badge }: FilterFormValues): Promise<void> => {
    if (!filterCascade.shed || !filterCascade.street) return
    setBarcodeUser(badge)
    const shelf = filterCascade.shelf || null
    const bin = filterCascade.bin || null
    lastSearch.current = {
      shed: filterCascade.shed,
      street: filterCascade.street,
      shelf,
      bin,
      page: 1,
      perPage: pagination.perPage,
    }
    await fetchStockLocations(
      badge,
      filterCascade.shed,
      filterCascade.street,
      shelf,
      bin,
      1,
      pagination.perPage,
    )
  }

  const onSubmitFilter = (event: React.FormEvent<HTMLFormElement>): void => {
    if (preventDoubleSubmit.current) return
    preventDoubleSubmit.current = true

    filterCascade.markTouched()
    handleSubmitFilter(onValidFilterSubmit)(event).finally(() => {
      preventDoubleSubmit.current = false
    })
  }

  const openAdjustmentDialog = (type: AdjustmentType, item: StockLocationRecord): void => {
    setAdjustmentItem(item)
    setAdjustmentType(type)
    setAdjustmentError(undefined)

    if (type === 'address') {
      addressCascade.reset()
      resetAddressForm({ justification: '' })
    }
    if (type === 'amount') {
      resetAmountForm({ amount: undefined, justification: '' })
    }
    if (type === 'expirationDate') {
      resetExpirationDateForm({ expirationDate: dayjs.utc(item.data_validade).format('YYYY-MM-DD') })
    }
  }

  const closeAdjustmentDialog = (): void => {
    setAdjustmentType(null)
    setAdjustmentItem(null)
    setAdjustmentError(undefined)
  }

  const onValidAddressSubmit = async ({ justification }: AddressFormValues): Promise<void> => {
    if (!addressCascade.isComplete) return
    if (!adjustmentItem) return

    try {
      dispatch(appActions.isLoading(true))
      const response = await updateStockAddress(
        barcodeUser,
        adjustmentItem.id_estoque_lote,
        adjustmentItem.id_modelo,
        adjustmentItem['id_localização'],
        adjustmentItem.id_lote,
        addressCascade.shed,
        addressCascade.street,
        addressCascade.shelf,
        addressCascade.bin,
        justification,
      )
      if (!response.isOk) {
        setAdjustmentError(response.data?.message ?? 'Falha ao atualizar o endereço no estoque!')
        return
      }
      closeAdjustmentDialog()
      await refetchLastSearch()
    } finally {
      dispatch(appActions.isLoading(false))
    }
  }

  const onSubmitAddress = (event: React.FormEvent<HTMLFormElement>): void => {
    if (preventDoubleAdjustmentSubmit.current) return
    preventDoubleAdjustmentSubmit.current = true

    addressCascade.markTouched()
    handleSubmitAddress(onValidAddressSubmit)(event).finally(() => {
      preventDoubleAdjustmentSubmit.current = false
    })
  }

  const onValidAmountSubmit = async ({ amount, justification }: AmountFormValues): Promise<void> => {
    if (!adjustmentItem) return

    try {
      dispatch(appActions.isLoading(true))
      const response = await updateStockQuantity(
        barcodeUser,
        adjustmentItem.id_estoque_lote,
        adjustmentItem.id_modelo,
        adjustmentItem['id_localização'],
        adjustmentItem.id_lote,
        fc?.id,
        amount,
        justification,
      )
      if (!response.isOk) {
        setAdjustmentError(response.data?.message ?? 'Falha ao ajustar a quantidade!')
        return
      }
      closeAdjustmentDialog()
      await refetchLastSearch()
    } finally {
      dispatch(appActions.isLoading(false))
    }
  }

  const onSubmitAmount = (event: React.FormEvent<HTMLFormElement>): void => {
    if (preventDoubleAdjustmentSubmit.current) return
    preventDoubleAdjustmentSubmit.current = true

    handleSubmitAmount(onValidAmountSubmit)(event).finally(() => {
      preventDoubleAdjustmentSubmit.current = false
    })
  }

  const onValidExpirationDateSubmit = async (
    { expirationDate }: ExpirationDateFormValues,
  ): Promise<void> => {
    if (!adjustmentItem) return

    try {
      dispatch(appActions.isLoading(true))
      const response = await updateStockExpirationDate(
        barcodeUser,
        adjustmentItem.id_estoque_lote,
        adjustmentItem.id_modelo,
        adjustmentItem['id_localização'],
        adjustmentItem.id_lote,
        expirationDate,
      )
      if (!response.isOk) {
        setAdjustmentError(response.data?.message ?? 'Falha ao ajustar a data de validade!')
        return
      }
      closeAdjustmentDialog()
      await refetchLastSearch()
    } finally {
      dispatch(appActions.isLoading(false))
    }
  }

  const onSubmitExpirationDate = (event: React.FormEvent<HTMLFormElement>): void => {
    if (preventDoubleAdjustmentSubmit.current) return
    preventDoubleAdjustmentSubmit.current = true

    handleSubmitExpirationDate(onValidExpirationDateSubmit)(event).finally(() => {
      preventDoubleAdjustmentSubmit.current = false
    })
  }

  const stockRows = useMemo<StockLocationRow[]>(() => stockLocations.map((item) => ({
    ...item,
    endereco: `${item.galpao}-${item.rua}-${item.posicao_pallete}-${item.escaninho_nivel}`,
    acao: <RowActionsMenu onSelect={(type) => openAdjustmentDialog(type, item)} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [stockLocations])

  return (
    <>
      <S.Container>
        <Typography variant='h5' fontWeight='bold'>Ajuste de Estoque</Typography>

        <Box
          component='form'
          onSubmit={onSubmitFilter}
          sx={{
            display: 'flex', flexWrap: 'wrap', gap: '1rem', width: '100%',
          }}
        >
          <S.CustomInputText
            name='barcodeUser'
            label='Crachá'
            type='password'
            inputProps={{
              ...registerFilter('barcodeUser', { required: requiredMessage }),
            }}
            error={!!filterErrors.barcodeUser}
            helperText={filterErrors.barcodeUser?.message}
          />
          <S.CustomSelect
            label='Galpão'
            dataTestId='shed-select'
            options={filterCascade.shedOptions}
            value={filterCascade.shedId}
            onChange={(event: SelectChangeEvent<unknown>) => filterCascade
              .onShedChange(event.target.value as number | '')}
            optionNone={false}
            fullWidth={false}
            searchBar
            searchBarDataTestId='shed-select-search-bar'
            error={filterCascade.touched && !filterCascade.shed}
            helperText={filterCascade.touched && !filterCascade.shed ? requiredMessage : undefined}
          />
          <S.CustomSelect
            label='Rua'
            dataTestId='street-select'
            options={filterCascade.streetOptions}
            value={filterCascade.streetId}
            onChange={(event: SelectChangeEvent<unknown>) => filterCascade
              .onStreetChange(event.target.value as number | '')}
            optionNone={false}
            fullWidth={false}
            searchBar
            searchBarDataTestId='street-select-search-bar'
            disabled={!filterCascade.shed}
            error={filterCascade.touched && !filterCascade.street}
            helperText={filterCascade.touched && !filterCascade.street ? requiredMessage : undefined}
          />
          <S.CustomSelect
            label='Estante'
            dataTestId='shelf-select'
            options={filterCascade.shelfOptions}
            value={filterCascade.shelfId}
            onChange={(event: SelectChangeEvent<unknown>) => filterCascade
              .onShelfChange(event.target.value as number | '')}
            fullWidth={false}
            searchBar
            searchBarDataTestId='shelf-select-search-bar'
            disabled={!filterCascade.street}
          />
          <S.CustomSelect
            label='Escaninho'
            dataTestId='bin-select'
            options={filterCascade.binOptions}
            value={filterCascade.binId}
            onChange={(event: SelectChangeEvent<unknown>) => filterCascade
              .onBinChange(event.target.value as number | '')}
            fullWidth={false}
            searchBar
            searchBarDataTestId='bin-select-search-bar'
            disabled={!filterCascade.shelf}
          />
          <S.CustomButton
            title='Filtrar'
            type='submit'
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
            disabled={isLoading}
          />
        </Box>

        <TableMui
          columns={[
            { id: 'id_lote', label: 'Lote', width: 90 },
            { id: 'cod_produto', label: 'Cod. Prod.', width: 120 },
            { id: 'descricao', label: 'Produto', width: 380 },
            {
              id: 'quantidade', label: 'Qtd', width: 70, align: 'right',
            },
            {
              id: 'data_validade', label: 'Validade', width: 110, isDate: true, dateFormat: 'DD/MM/YYYY',
            },
            { id: 'endereco', label: 'Endereço', width: 150 },
            {
              id: 'acao', label: 'Ações', width: 70, useSorting: false,
            },
          ]}
          emptyLabel='Utilize o filtro para buscar o estoque'
          rows={stockRows}
          rowCount={stockRows.length}
          minHeight='64vh'
          maxHeight='64vh'
          enablePagination
          rowsPerPageOptions={[15, 20, 25, 50, 75, 100]}
          rowsPerPageLimit={pagination.perPage}
          currentPage={pagination.currentPage - 1}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          getPaginatedItems={getPaginatedItems}
          resetPageOnRowsChange={false}
          hasSorting
          thinLine
        />
      </S.Container>

      <AdjustmentDialog
        open={adjustmentType === 'address'}
        title={dialogTitles.address}
        onClose={closeAdjustmentDialog}
        onSubmit={onSubmitAddress}
        error={adjustmentError}
      >
        <S.CustomSelect
          label='Galpão'
          dataTestId='address-shed-select'
          options={addressCascade.shedOptions}
          value={addressCascade.shedId}
          onChange={(event: SelectChangeEvent<unknown>) => addressCascade
            .onShedChange(event.target.value as number | '')}
          optionNone={false}
          fullWidth
          searchBar
          searchBarDataTestId='address-shed-select-search-bar'
          error={addressCascade.touched && !addressCascade.shed}
          helperText={addressCascade.touched && !addressCascade.shed ? requiredMessage : undefined}
        />
        <S.CustomSelect
          label='Rua'
          dataTestId='address-street-select'
          options={addressCascade.streetOptions}
          value={addressCascade.streetId}
          onChange={(event: SelectChangeEvent<unknown>) => addressCascade
            .onStreetChange(event.target.value as number | '')}
          optionNone={false}
          fullWidth
          searchBar
          searchBarDataTestId='address-street-select-search-bar'
          disabled={!addressCascade.shed}
          error={addressCascade.touched && !addressCascade.street}
          helperText={addressCascade.touched && !addressCascade.street ? requiredMessage : undefined}
        />
        <S.CustomSelect
          label='Estante'
          dataTestId='address-shelf-select'
          options={addressCascade.shelfOptions}
          value={addressCascade.shelfId}
          onChange={(event: SelectChangeEvent<unknown>) => addressCascade
            .onShelfChange(event.target.value as number | '')}
          optionNone={false}
          fullWidth
          searchBar
          searchBarDataTestId='address-shelf-select-search-bar'
          disabled={!addressCascade.street}
          error={addressCascade.touched && !addressCascade.shelf}
          helperText={addressCascade.touched && !addressCascade.shelf ? requiredMessage : undefined}
        />
        <S.CustomSelect
          label='Escaninho'
          dataTestId='address-bin-select'
          options={addressCascade.binOptions}
          value={addressCascade.binId}
          onChange={(event: SelectChangeEvent<unknown>) => addressCascade
            .onBinChange(event.target.value as number | '')}
          optionNone={false}
          fullWidth
          searchBar
          searchBarDataTestId='address-bin-select-search-bar'
          disabled={!addressCascade.shelf}
          error={addressCascade.touched && !addressCascade.bin}
          helperText={addressCascade.touched && !addressCascade.bin ? requiredMessage : undefined}
        />
        <S.CustomTextArea
          name='justification'
          label='Justificativa'
          multiline
          rows={3}
          fullWidth
          inputProps={{ ...registerAddress('justification', { required: requiredMessage }) }}
          error={!!addressErrors.justification}
          helperText={addressErrors.justification?.message}
        />
      </AdjustmentDialog>

      <AdjustmentDialog
        open={adjustmentType === 'amount'}
        title={dialogTitles.amount}
        onClose={closeAdjustmentDialog}
        onSubmit={onSubmitAmount}
        error={adjustmentError}
      >
        <S.CustomInputText
          name='amount'
          label='Quantidade'
          type='number'
          fullWidth
          inputProps={{
            ...registerAmount('amount', {
              required: requiredMessage,
              valueAsNumber: true,
              min: { value: 0, message: 'Quantidade inválida' },
            }),
          }}
          error={!!amountErrors.amount}
          helperText={amountErrors.amount?.message}
        />
        <S.CustomTextArea
          name='justification'
          label='Justificativa'
          multiline
          rows={3}
          fullWidth
          inputProps={{ ...registerAmount('justification', { required: requiredMessage }) }}
          error={!!amountErrors.justification}
          helperText={amountErrors.justification?.message}
        />
      </AdjustmentDialog>

      <AdjustmentDialog
        open={adjustmentType === 'expirationDate'}
        title={dialogTitles.expirationDate}
        onClose={closeAdjustmentDialog}
        onSubmit={onSubmitExpirationDate}
        error={adjustmentError}
      >
        <S.CustomInputText
          name='expirationDate'
          label='Data de Validade'
          type='date'
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ ...registerExpirationDate('expirationDate', { required: requiredMessage }) }}
          error={!!expirationDateErrors.expirationDate}
          helperText={expirationDateErrors.expirationDate?.message}
        />
      </AdjustmentDialog>
    </>
  )
}

export default Adjustments
